import React, { type ReactElement } from 'react';
import { capitalize, toInteger } from 'lodash';
import { Modal, ModalBody, ModalHeader } from 'reactstrap';
import Alert from 'atom-components/alert';
import FormButtonsContainer from 'atom-components/form-buttons-container';
import Loading from 'atom-components/loading';
import Table from 'atom-components/table-lite/table';
import { pluralise } from 'helpers/string.helpers';
import type { BaseCustomerUser } from 'services/assist-api/entities/customer-user/types';
import type { TestInstanceResponseMarkerWithUser } from 'services/assist-api/entities/test-instance-response-marker/types';
import TableRow from './table-row';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onDeallocate: () => void;
  responseId: number;
  markersOfResponse: TestInstanceResponseMarkerWithUser[];
  organisationUsers: BaseCustomerUser[];
  isLoading: boolean;
};

export default function DeallocateMarkersModal({
  isOpen,
  onClose,
  onDeallocate,
  responseId,
  markersOfResponse,
  organisationUsers,
  isLoading
}: Props): ReactElement {
  return (
    <Modal isOpen={isOpen} size="md">
      <ModalHeader toggle={onClose}>Deallocate Markers (or Reallocate Response)</ModalHeader>
      <ModalBody>
        <div>
          <p>
            {capitalize(pluralise('this', markersOfResponse.length))}{' '}
            {pluralise('marker', markersOfResponse.length)}{' '}
            {pluralise('is', markersOfResponse.length)} allocated to assessment{' '}
            <b>response #{responseId}</b>:
          </p>
          {markersOfResponse.length === 0 && (
            <Alert variant="success">No markers allocated to this response.</Alert>
          )}
          {markersOfResponse.length > 0 && (
            <Table>
              <Table.Head>
                <Table.Row>
                  <Table.Header>Marker</Table.Header>
                  <Table.Header>Actions</Table.Header>
                </Table.Row>
              </Table.Head>
              <Table.Body>
                {isLoading && (
                  <Table.Row>
                    <Table.DataCell colSpan={2}>
                      <div className="container text-center m-10">
                        <Loading overlay={false} positionAbsolute={false} />
                      </div>
                    </Table.DataCell>
                  </Table.Row>
                )}
                {!isLoading &&
                  organisationUsers.map(
                    (organisationUser: BaseCustomerUser): ReactElement => (
                      <TableRow
                        key={organisationUser.id}
                        responseId={responseId}
                        allocatedMarkerId={toInteger(
                          markersOfResponse.find((marker) => marker.user.id === organisationUser.id)
                            ?.id
                        )}
                        allocatedOrganisationUser={organisationUser}
                        onDeallocate={onDeallocate}
                        isLoading={isLoading}
                      />
                    )
                  )}
              </Table.Body>
            </Table>
          )}
        </div>
        <FormButtonsContainer>
          <button type="button" className="btn btn-primary" onClick={onClose}>
            Continue
          </button>
        </FormButtonsContainer>
      </ModalBody>
    </Modal>
  );
}
